import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "parentForm",
    "existingUserInfo",
    "submitButton",
  ];

  static values = {
    searchUrl: String,
    noResultsText: String,
    noResultsHelpText: String,
    selectButtonText: String,
    foundUsersTitle: String,
    selectedUserTitle: String,
    createNewInsteadText: String,
    associateExistingButtonText: String,
    createNewButtonText: String,
    staffSourceLabel: String,
  };

  connect() {
    this.timeout = null;
    this.selectedUserId = null;
    console.log("Parent user search controller connected");
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  search() {
    const query = this.searchInputTarget.value.trim();

    // Clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  async performSearch(query) {
    if (query.length < 2) {
      this.hideSearchResults();
      return;
    }

    try {
      const response = await fetch(
        `${this.searchUrlValue}?search_term=${encodeURIComponent(query)}`,
      );
      const data = await response.json();

      this.displaySearchResults(data.users);
    } catch (error) {
      console.error("Search error:", error);
      this.hideSearchResults();
    }
  }

  displaySearchResults(users) {
    if (users.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <svg class="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.22 0-4.239-.732-5.937-1.972"></path>
          </svg>
          <p>${this.noResultsTextValue}</p>
          <p class="text-sm">${this.noResultsHelpTextValue}</p>
        </div>
      `;
      this.searchResultsTarget.classList.remove("hidden");
      return;
    }

    const resultsHtml = users
      .map((user) => {
        const sourceLabel =
          user.source === "school_staff"
            ? `<span class="inline-flex items-center px-2 py-0.5 rounded-[10px] text-xs font-medium bg-gray-100 text-[#3F4E4F] ml-2">${this.staffSourceLabelValue}</span>`
            : "";
        return `
        <div class="flex items-center justify-between p-3 border-b border-gray-200 last:border-b-0">
          <div class="flex-1">
            <h4 class="font-medium text-gray-900">${user.full_name}${sourceLabel}</h4>
            <div class="text-sm text-gray-500">
              ${user.email ? `<p>📧 ${user.email}</p>` : ""}
              ${user.phonenumber ? `<p>📞 ${user.phonenumber}</p>` : ""}
            </div>
          </div>
          <button
            type="button"
            class="ds-btn ds-btn--primary ds-btn--sm"
            data-action="click->parent-user-search#selectUser"
            data-user-id="${user.id || ""}"
            data-user-firstname="${user.firstname || ""}"
            data-user-lastname="${user.lastname || ""}"
            data-user-email="${user.email || ""}"
            data-user-phonenumber="${user.phonenumber || ""}"
            data-user-data="${encodeURIComponent(JSON.stringify(user))}"
          >
            ${this.selectButtonTextValue}
          </button>
        </div>
      `;
      })
      .join("");

    this.searchResultsTarget.innerHTML = `
      <div class="bg-white border border-gray-200 rounded-md shadow-sm">
        <div class="p-3 bg-gray-50 border-b border-gray-200">
          <h3 class="text-sm font-medium text-gray-900">${this.foundUsersTitleValue}</h3>
        </div>
        ${resultsHtml}
      </div>
    `;

    this.searchResultsTarget.classList.remove("hidden");
  }

  selectUser(event) {
    const button = event.currentTarget;
    const userId = button.dataset.userId;
    const firstname = button.dataset.userFirstname;
    const lastname = button.dataset.userLastname;
    const email = button.dataset.userEmail;
    const phonenumber = button.dataset.userPhonenumber;

    // Récupérer toutes les données de l'utilisateur
    const userData = JSON.parse(decodeURIComponent(button.dataset.userData));

    this.selectedUserId = userId || null;

    this.hideSearchResults();
    this.showExistingUserInfo(firstname, lastname, email, phonenumber);
    this.prefillFormWithUserData(userData);
    this.updateSubmitButton(!!userId);
  }

  showExistingUserInfo(firstname, lastname, email, phonenumber) {
    this.existingUserInfoTarget.innerHTML = `
      <div class="bg-cta-shared border border-cta rounded-sm p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-cta" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h4 class="text-sm font-medium text-cta-darker">${this.selectedUserTitleValue}</h4>
            <div class="mt-1 text-sm text-cta-darker">
              <p><strong>${(lastname || "").toUpperCase()} ${firstname || ""}</strong></p>
              <p>📧 ${email}</p>
              ${phonenumber ? `<p>📞 ${phonenumber}</p>` : ""}
            </div>
            <div class="mt-3">
              <button
                type="button"
                class="ds-link text-sm underline"
                data-action="click->parent-user-search#createNew"
              >
                ${this.createNewInsteadTextValue}
              </button>
            </div>
          </div>
        </div>
      </div>
      ${this.selectedUserId ? `<input type="hidden" name="parent[existing_user_id]" value="${this.selectedUserId}">` : ""}
    `;
    this.existingUserInfoTarget.classList.remove("hidden");
  }

  prefillFormWithUserData(userData) {
    const isExistingUser = !!userData.id;
    // Pré-remplir les champs de base depuis le user
    this.setInputValue('[name="parent[first_name]"]', userData.firstname, isExistingUser);
    this.setInputValue('[name="parent[last_name]"]', userData.lastname, isExistingUser);
    this.setInputValue('[name="parent[email]"]', userData.email, isExistingUser);
    this.setInputValue('[name="parent[phone]"]', userData.phonenumber);

    // Pré-remplir depuis parent_info si disponible
    if (userData.parent_info) {
      const parentInfo = userData.parent_info;
      if (parentInfo.first_name)
        this.setInputValue(
          '[name="parent[first_name]"]',
          parentInfo.first_name,
          true,
        );
      if (parentInfo.last_name)
        this.setInputValue(
          '[name="parent[last_name]"]',
          parentInfo.last_name,
          true,
        );
      if (parentInfo.phone)
        this.setInputValue('[name="parent[phone]"]', parentInfo.phone);
      if (parentInfo.email)
        this.setInputValue('[name="parent[email]"]', parentInfo.email, true);
    }

    // Pré-remplir l'adresse si disponible
    if (userData.address_info) {
      const addressInfo = userData.address_info;
      this.setInputValue(
        '[name="parent[address_attributes][complete_address]"]',
        addressInfo.complete_address,
      );
      this.setInputValue(
        '[name="parent[address_attributes][street_address]"]',
        addressInfo.street_address,
      );
      this.setInputValue(
        '[name="parent[address_attributes][address_line2]"]',
        addressInfo.address_line2,
      );
      this.setInputValue(
        '[name="parent[address_attributes][landmark]"]',
        addressInfo.landmark,
      );
      this.setInputValue(
        '[name="parent[address_attributes][quarter]"]',
        addressInfo.quarter,
      );
      this.setInputValue(
        '[name="parent[address_attributes][postal_box]"]',
        addressInfo.postal_box,
      );
      this.setInputValue(
        '[name="parent[address_attributes][postal_code]"]',
        addressInfo.postal_code,
      );
      this.setInputValue(
        '[name="parent[address_attributes][city]"]',
        addressInfo.city,
      );
      this.setInputValue(
        '[name="parent[address_attributes][region]"]',
        addressInfo.region,
      );
      this.setInputValue(
        '[name="parent[address_attributes][country]"]',
        addressInfo.country,
      );
      this.setInputValue(
        '[name="parent[address_attributes][country_code]"]',
        addressInfo.country_code,
      );
      this.setInputValue(
        '[name="parent[address_attributes][latitude]"]',
        addressInfo.latitude,
      );
      this.setInputValue(
        '[name="parent[address_attributes][longitude]"]',
        addressInfo.longitude,
      );
      this.setInputValue(
        '[name="parent[address_attributes][notes]"]',
        addressInfo.notes,
      );
    }
  }

  setInputValue(selector, value, readonly = false) {
    const input = this.parentFormTarget.querySelector(selector);
    if (!input || value === null || value === undefined || value === "") {
      return;
    }

    // Gérer les différents types d'inputs
    if (input.tagName === "SELECT") {
      console.log("SELECT", selector, value);
      // Pour les selects, trouver l'option correspondante
      const option = Array.from(input.options).find(
        (opt) => opt.value === value || opt.text === value,
      );
      if (option) {
        input.value = option.value;
        console.log("INPUT", input.value);
        console.log("OPTION", option);
        // Déclencher l'événement change pour les selects avec listeners
        // input.dispatchEvent(new Event("change", { bubbles: true }));
        // if data-slim-initialized="true" then set it to false and delete the sibling dit .ss-main and finally call initSlimSelects(selector)
        if (input.dataset.slimInitialized === "true") {
          input.dataset.slimInitialized = "false";
          // remove display none on input
          input.style.display = "";
          input.nextElementSibling.remove();
          initSlimSelects(selector);
          input.style.display = "none";
        }
      } else {
        // Debug: afficher les options disponibles si aucune correspondance
        console.log(
          `No matching option found for selector: ${selector}, value: ${value}`,
        );
        console.log(
          "Available options:",
          Array.from(input.options).map((opt) => ({
            value: opt.value,
            text: opt.text,
          })),
        );
      }
    } else if (input.type === "date") {
      // Pour les inputs date, s'assurer que le format est YYYY-MM-DD
      const dateValue = this.formatDateForInput(value);
      if (dateValue) {
        input.value = dateValue;
      }
    } else {
      // Pour les autres inputs (text, email, tel, etc.)
      input.value = value;
    }

    // Appliquer le style readonly si nécessaire
    if (readonly) {
      if (input.tagName === "SELECT") {
        input.disabled = true;
        input.classList.add("bg-gray-100");
      } else {
        input.readOnly = true;
        input.classList.add("bg-gray-100");
      }
    }
  }

  formatDateForInput(dateValue) {
    if (!dateValue) return null;

    // Si c'est déjà au format YYYY-MM-DD
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      return dateValue;
    }

    // Essayer de parser la date
    try {
      const date = new Date(dateValue);
      if (!isNaN(date.getTime())) {
        // Formater en YYYY-MM-DD
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    } catch (e) {
      console.error("Error formatting date:", e);
    }

    return null;
  }

  createNew() {
    this.selectedUserId = null;
    this.hideExistingUserInfo();
    this.existingUserInfoTarget.innerHTML = "";
    this.clearForm();
    this.updateSubmitButton(false);
    this.searchInputTarget.value = "";
    this.hideSearchResults();
  }

  clearForm() {
    // Réinitialiser tous les champs du formulaire
    const inputs = this.parentFormTarget.querySelectorAll(
      'input[type="text"], input[type="email"], input[type="tel"], textarea, select',
    );

    inputs.forEach((input) => {
      if (input.tagName === "SELECT") {
        // Pour les selects, réinitialiser à la première option (souvent vide ou "Sélectionner...")
        input.selectedIndex = 0;
        input.disabled = false;
        input.classList.remove("bg-gray-100");
      } else {
        input.value = "";
        input.readOnly = false;
        input.classList.remove("bg-gray-100");
      }
    });
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden");
  }

  hideExistingUserInfo() {
    this.existingUserInfoTarget.classList.add("hidden");
  }

  updateSubmitButton(isExisting) {
    if (!this.hasSubmitButtonTarget) return;

    // L'habillage du bouton reste piloté par le Design System (ds-btn ds-btn--primary).
    // On ne change que le libellé selon le mode (création vs association).
    const label = isExisting
      ? this.associateExistingButtonTextValue
      : this.createNewButtonTextValue;
    const btn = this.submitButtonTarget;
    // f.submit rend un <input type="submit"> (libellé = value) ;
    // un éventuel <button> utiliserait textContent.
    if (btn.tagName === "INPUT") {
      btn.value = label;
    } else {
      btn.textContent = label;
    }
  }
}
